import * as _ from 'lodash';
import { LookupFieldType } from "~/enums/lookupFieldType";
import { ModelType } from '~/enums/modelType';
import { ObjectModelRecordHolderType } from "~/enums/objectModelRecordHolderType";
import { PermitteeType, PriorStepTypes } from "~/enums/permitteeType";
import { GenericType } from '~/interfaces/genericTypeInterfaces';
import { Hash } from '~/interfaces/hashInterfaces';
import { IImposter, IImposterFactory, IImposterOptions } from '~/interfaces/imposterInterfaces';
import { ITrackedModel } from "~/interfaces/modelInterfaces";
import Imposter from '~/models/imposter';
import { DurationUnit } from "../../../../common/enums/durationUnit";
import { ObjectHelper } from "../../../../common/helpers/objectHelper";
import IRepo from "../../../../common/interfaces/repo";
import Validatable from "../../../../common/mixins/validatable";
import ConditionalRuleSet from "../../../../common/models/conditionalRules/conditionalRuleSet";
import ConditionalRuleSetFactory from "../../../../common/models/conditionalRules/conditionalRuleSetFactory";
import WorkflowStepValidation from "../../../../common/models/navigableValidations/workflowStepValidation";
import { RelationType } from "../../../../common/models/relationType";
import FormBuilderModel from "../../../../common/models/tracked/formBuilderModel";
import Repo from "../../../../common/state/modules/repo";
import Selected from "../../store/modules/selected";
import FormElementConditionalFields from "../conditionalRules/formElementConditionalFields";
import Form from "../form";
import FormElement from "../formElements/formElement";
import ThirdPartyCompleter from "../formElements/templates/thirdPartyCompleter";
import FormSection from "../formSections/formSection";
import FormSectionFactory from "../formSections/formSectionFactory";
import Notification from "../notifications/notification";
import NotificationFactory from "../notifications/notificationFactory";
import { NotificationTriggerType } from "../notifications/notificationTriggerType";
import PaymentLineItem from "../paymentLineItems/paymentLineItem";
import PaymentLineItemFactory from "../paymentLineItems/paymentLineItemFactory";
import UserDefinedField from "../userDefinedFields/userDefinedField";
import ApprovalStep from "./approvalStep";
import { AutoAssignMode } from "./autoAssignMode";
import OverdueSubmissionAction from "./overdueSubmissionAction";
import PaymentInvoice from "./paymentInvoice";
import { PaymentType } from "~/enums/workflow/paymentType";
import Submitter from "./submitter";
import WorkflowDefinition from "./workflowDefinition";
import WorkflowStepAction from "./workflowStepAction";
import WorkflowStepActionJumpToFactory from "./workflowStepActionJumpToFactory";
import DataEditor from "./workflowStepPermittees/dataEditor";
import Mover from "./workflowStepPermittees/mover";
import StatusViewer from "./workflowStepPermittees/statusViewer";
import Viewer from "./workflowStepPermittees/viewer";
import { WorkflowType } from "~/enums/forms/workflowSteps/workflowType";


export type WorkflowStepPermitteeType = Mover | Viewer | StatusViewer | DataEditor;

export interface IWorkflowStep extends ITrackedModel, IImposterFactory {
  workflow_type: WorkflowType
  titleOrDefault: string
}

export default class WorkflowStep extends Validatable(FormBuilderModel) implements IWorkflowStep {
  workflow_type: WorkflowType;
  title: string;
  sort_order: number;
  num_of_approvers: number;
  is_auto_assigned: boolean;
  has_sla: boolean;
  has_sla_goal: boolean;
  sla_goal_amount: number;
  sla_goal_unit: number;
  sla_warn_amount: number;
  sla_warn_unit: number;
  sla_end_amount: number;
  sla_end_unit: number;
  application_status: string;
  auto_assign_mode: AutoAssignMode;
  submitter: Submitter;
  status_viewers: { [key: string]: StatusViewer} = {};
  viewers: { [key: string]: Viewer} = {};
  movers: { [key: string]: Mover} = {};
  notifications: { [key: string]: Notification} = {};
  payment_line_items: { [key: string]: PaymentLineItem} = {};
  user_defined_fields: { [key: string]: UserDefinedField} = {};
  payment_invoice: PaymentInvoice;
  actions: { [key: string]: WorkflowStepAction} = {};
  overdue_submission_actions: { [key: string]: OverdueSubmissionAction} = {};
  form_section: FormSection; // dangerous, will this work?
  has_form_section: boolean;
  has_approval: boolean;
  license_id: string;
  license: any;
  except = ['form', 'form_section', 'submitter', 'viewers', 'movers', 'status_viewers', 'notifications', 'createEmbeddedObject', 'payment_line_items', 'payment_invoice', 'overdue_submission_actions' ,'actions', 'license', 'mapping', 'process_flow'];
  is_data_editable: boolean;
  data_editors: { [key: string]: DataEditor} = {};
  editable_statuses: string[];
  resubmission_due_date_type: number;
  submission_due_date_type: number;
  submission_has_due_date: boolean;
  submission_due_date_month: number;
  submission_due_date_day: number;
  submission_period_end_unit: number;
  submission_period_end_amount: number;
  resubmission_period_end_unit: number;
  resubmission_period_end_amount: number;
  overdue_submission_actions_type: number;
  overdue_application_status: string;
  overdue_submission_jump_to_client_id: string;
  run_pre_population_mapping: boolean;
  run_pre_process_flow: boolean;
  pre_population_mapping_id: string;
  certificate_id: string;
  certificate: {
    title: string;
    is_published: string;
  }
  payment_type: PaymentType;

  process_flow_id: string;
  process_flow: {
    id: string,
    title: string,
    is_published: string,
    variables: Hash,
  };

  object_model_variable_reference_id: string;
  form_submission_variable_reference_id: string;

  static repo: IRepo<WorkflowStep> = new Repo<WorkflowStep>('workflowSteps', WorkflowStep, { onFirstAdd: (item) => { Selected.workflowStep.set(item); } });

  constructor(form?) {
    super();
    this.rootModel = form;
    this.defineProperty('title', '');
    this.defineProperty('num_of_approvers', 1);
    this.defineProperty('is_auto_assigned', true);
    this.defineProperty('auto_assign_mode', AutoAssignMode.leastPending);
    this.defineProperty('has_sla', false);
    this.defineProperty('has_sla_goal', false);
    this.defineProperty('sla_goal_amount', 0);
    this.defineProperty('sla_goal_unit', DurationUnit.hour);
    this.defineProperty('sla_warn_amount', 0);
    this.defineProperty('sla_warn_unit', DurationUnit.hour);
    this.defineProperty('sla_end_amount', 0);
    this.defineProperty('sla_end_unit', DurationUnit.hour);
    this.defineProperty('is_data_editable', false);
    this.defineProperty('editable_statuses', []);
    this.defineProperty('run_pre_population_mapping', false);
    this.defineProperty('run_pre_process_flow', false);
    this.defineProperty('submission_has_due_date', false);
    this.defineProperty('resubmission_due_date_type', 0);
    this.defineProperty('overdue_submission_action_type', 0)
    this.defineProperty('process_flow_id', null, false, null,() => {
      this.object_model_variable_reference_id = null;
      this.form_submission_variable_reference_id = null
    });
    this.defineProperty('object_model_variable_reference_id', null)
    this.defineProperty('form_submission_variable_reference_id', null)

    for (const definition of FormElementConditionalFields.WORKFLOW_STEP_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(parent, definition, json, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }
  }

  //******************** VALIDATABLE ********************//
  _validation: WorkflowStepValidation<WorkflowStep> = new WorkflowStepValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return propName;
    }
  }
  //******************** VALIDATABLE END ********************//

  get UNTRACKED_PROPERTIES(): string[] {
    return [
      "pre_population_mapping",
      "process_flow"
    ];
  }


  get titleOrDefault(): string { return null; }

  get referenceNameOrDefault(): string {
    return this.titleOrDefault;
  }

  get isThirdPartyMultiSection(): boolean { return false; }

  get isMultiSectionSet(): boolean { return false; }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      'viewers': { type: Viewer, relationType: RelationType.dictionary },
      'status_viewers': { type: StatusViewer, relationType: RelationType.dictionary },
      'movers': { type: Mover, relationType: RelationType.dictionary },
      'notifications': { type: Notification, relationType: RelationType.dictionary },
      'payment_line_items': { type: PaymentLineItem, relationType: RelationType.dictionary },
      'payment_invoice': { type: PaymentInvoice, relationType: RelationType.singleObject },
      'actions': { type: WorkflowStepAction, relationType: RelationType.dictionary },
      'form_section': { type: FormSection, relationType: RelationType.singleObject },
      'submitter': { type: Submitter, relationType: RelationType.singleObject },
      'data_editors': { type: DataEditor, relationType: RelationType.dictionary },
      'overdue_submission_actions':{type: OverdueSubmissionAction, relationType: RelationType.dictionary},
      'user_defined_fields': {type: UserDefinedField, relationType: RelationType.dictionary },
    };
    for (const {fieldName} of FormElementConditionalFields.WORKFLOW_STEP_FIELDS) {
      models[`${fieldName}_conditional_rule_sets`] = { type: ConditionalRuleSet, relationType: RelationType.dictionary };
    }
    return models;
  }

  createEmbeddedObject = {
    form_section: (parent: WorkflowStep, json): FormSection => {
      return FormSectionFactory.create(parent);
    },
    submitter: (parent, json): Submitter => { return new Submitter(parent, json); },
    viewers: (parent, json): Viewer => { return new Viewer(parent, json); },
    status_viewers: (parent, json): StatusViewer => { return new StatusViewer(parent, json); },
    movers: (parent, json): Mover => { return new Mover(parent, json); },
    notifications: (parent, json): Notification => { return NotificationFactory.create(parent);},
    payment_line_items: (parent, json): PaymentLineItem => { return PaymentLineItemFactory.create(parent, json, true, false);},
    payment_invoice: (parent, json): PaymentInvoice => { return new PaymentInvoice(parent, json) },
    actions: (parent, json): WorkflowStepAction => { return new WorkflowStepAction(parent, json); },
    overdue_submission_actions: (parent, json): OverdueSubmissionAction => { return new OverdueSubmissionAction(parent, json); },
    data_editors: (parent, json): DataEditor => { return new DataEditor(parent, json); },
    user_defined_fields: (parent, json): UserDefinedField => { return new UserDefinedField(parent, json); },
  };

  get form(): Form {
    return this.rootModel
  }

  static get completerTypes() {
    return [WorkflowType.approval, WorkflowType.form, WorkflowType.formSection, WorkflowType.payment];
  }

  get hasTitle() {
    return this.title && this.title.length > 0;
  }

  get getWorkflowName() {
    return WorkflowDefinition.getWorkflowName(this);
  }

  get getWorkflowIcon(): string {
    return WorkflowDefinition.getWorkflowIcon(this);
  }

  get isInitialStep(): boolean {
    return this.workflow_type === WorkflowType.initialStep;
  }

  get isCompleterStep(): boolean {
    return WorkflowStep.completerTypes.includes(this.workflow_type);
  }

  get isApprovalOrFormSectionStep(): boolean {
    return this.workflow_type === WorkflowType.approval;
  }

  get isFormStep(): boolean {
    return this.workflow_type === WorkflowType.form;
  }

  get isMappingStep(): boolean {
    return this.workflow_type === WorkflowType.mapping;
  }

  get isPaymentStep(): boolean {
    return this.workflow_type === WorkflowType.payment;
  }

  get isProcessFlowStep(): boolean {
    return this.workflow_type === WorkflowType.processFlow;
  }

  get isLicenseStatusChangeStep(): boolean {
    // step was formerly known as an award step, and remains so on the backend
    return this.workflow_type === WorkflowType.licenseStatusChange;
  }

  get isCertificateStep(): boolean {
    return this.workflow_type === WorkflowType.certificate;
  }

  get isLinkedFormStep(): boolean {
    return this.workflow_type === WorkflowType.form
  }

  get areDataViewersConfigurable(): boolean {
    return this.has_form_section || this.isCertificateStep || this.isLinkedFormStep
  }

  get areDataEditorsConfigurable(): boolean {
    return (this.isInitialStep || this.isApprovalOrFormSectionStep) && this.submitter.reference_type !== PermitteeType.thirdParty
  }

  // override in subclass
  get canConfigureActions(): boolean {
    return false;
  }

  // override in subclass
  get canAddActions(): boolean {
    return false;
  }

  updateJumpToForEachParent(action: Function): void {
    for (const clientId in this.rootModel.workflow_steps) {
      const workflowStep = this.rootModel.workflow_steps[clientId];
      for (const actionKey in workflowStep.actions) {
        const workflowStepAction = workflowStep.actions[actionKey];
        if (workflowStepAction.jump_to_client_id === this.client_id) {
          workflowStepAction.jump_to_client_id = action(workflowStepAction.jump_to_client_id);
        }
        const ruleSets = workflowStepAction.getSortedJumpToConditionals();
        for (const ruleSet of ruleSets) {
          if (ruleSet.conditional_value.to_client_id === this.client_id) {
            ruleSet.conditional_value.to_client_id = action(ruleSet.conditional_value.to_client_id);
          }
        }
      }
    }
  }

  canJumpTo(clientId: string): boolean {
    return this.checkJumpToRecursively(clientId, {});
  }

  protected checkJumpToRecursively(clientId, clientIdsSoFar): boolean {
    clientIdsSoFar[this.client_id] = true;
    for (const action of Array.from(this.actionsArray())) {
      const actionJumpToClientId = action.jump_to_client_id;
      if (actionJumpToClientId === clientId) return true;
      if (!clientIdsSoFar[actionJumpToClientId] && actionJumpToClientId !== '' &&
        this.rootModel.workflow_steps[actionJumpToClientId] && this.rootModel.workflow_steps[actionJumpToClientId].checkJumpToRecursively(clientId, clientIdsSoFar)) {
        return true;
      }
      const jumpToConditionals = action.getJumpToConditionals();
      for(const key in jumpToConditionals) {
        const jumpToClientId = jumpToConditionals[key].conditional_value.to_client_id;
        if (jumpToClientId === clientId) return true;
        if (!clientIdsSoFar[jumpToClientId] && jumpToClientId !== '' &&
          this.rootModel.workflow_steps[jumpToClientId] && this.rootModel.workflow_steps[jumpToClientId].checkJumpToRecursively(clientId, clientIdsSoFar)) {
          return true;
        }
      }
    }
    return false;
  }

  get nextSteps(): any[] {
    if (Object.keys(this.actions).length === 1) {
      return this.defaultAction.nextSteps;
    } else {
      return Array.from(this.orderedActions);
    }
  }

  get jump_to_client_id_conditional_rule_sets() {
    return this.defaultAction.getConditionalRuleSets("jump_to");
  }

  get jump_to_client_id() {
    return this.defaultAction.jump_to_client_id;
  }

  set jump_to_client_id(val) {
    this.defaultAction.jump_to_client_id = val;
  }

  get orderedActions() {
    return (<any>Object).values(this.actions).sort((a, b) => a.sort_order - b.sort_order);
  }

  get orderedOverdueSubmissionActions() {
    return (<any>Object).values(this.overdue_submission_actions).sort((a, b) => a.sort_order - b.sort_order);
  }

  get defaultAction() {
    for (const key in this.actions) {
      return this.actions[key];
    }
    return null;
  }

  get defaultJumpTo() {
    return this.defaultAction.jump_to;
  }

  get rootName(): string {
    return 'workflow_steps';
  }

  get elementName(): string {
    return 'workflow_step';
  }

  appendStatusViewer(props): StatusViewer {
    const statusViewer = new StatusViewer(this, props);
    this.trackCreationDeletion(statusViewer.client_id, null, statusViewer.asJson(), statusViewer);
    return statusViewer;
  }

  appendViewer(props): Viewer {
    const viewer = new Viewer(this, props);
    this.trackCreationDeletion(viewer.client_id, null, viewer.asJson(), viewer);
    return viewer;
  }

  appendMover(props): Mover {
    const mover = new Mover(this, props);
    this.trackCreationDeletion(mover.client_id, null, mover.asJson(), mover);
    return mover;
  }

  appendDataEditor(props): DataEditor {
    const dataEditor = new DataEditor(this, props);
    this.trackCreationDeletion(dataEditor.client_id, null, dataEditor.asJson(), dataEditor);
    return dataEditor;
  }

  createDefaultViewers(): void {
    this.appendViewer({
      reference_id: PermitteeType.stepCompleters.toString(),
      reference_type: PermitteeType.stepCompleters
    });
  }

  get previousWorkflowStepsWithFormSection(): ApprovalStep[] {
    return this.rootModel.workflowStepsWithFormSections.filter(ws => this.isChildOf(ws));
  }

  get previousFormSections(): FormSection[] {
    return this.previousWorkflowStepsWithFormSection.map(ws => ws.form_section);
  }

  get currentFormSection() : FormSection {
    return this.rootModel.currentFormSection();
  }

  get referenceableThirdPartyElements(): ThirdPartyCompleter[] {
    const elements: ThirdPartyCompleter[] = [];
    if (this.isInitialStep) {
      elements.push(...this.currentFormSection.thirdPartyElements);
    } else {
      this.previousFormSections.forEach(fs => elements.push(...fs.thirdPartyElements));
    }
    return _.sortBy(elements, fe => fe.referenceNameOrDefault.toLowerCase());
  }

  get hasReferenceableThirdPartyElements(): boolean {
    return this.referenceableThirdPartyElements.length > 0;
  }

  get referenceableLookupObjectModelFields(): FormElement[] {
    let elements: FormElement[] = [];
    if (this.isInitialStep) {
      elements.push(...this.currentFormSection.lookupFieldElements);
    } else {
      this.previousFormSections.forEach(fs => elements.push(...fs.lookupFieldElements));
    }
    elements = elements.filter(lf => lf.lookup_type === LookupFieldType.objectModels && lf.object_model_instance_selection_limit === 1 && lf.object_model.holder_type !== ObjectModelRecordHolderType.None);
    return _.sortBy(elements, fe => fe.referenceNameOrDefault.toLowerCase());
  }

  get hasReferenceableLookupObjectModelFields(): boolean {
    return this.referenceableLookupObjectModelFields.length > 0;
  }

  get previousWorkflowStepsWithCompleter(): WorkflowStep[] {
    return this.rootModel.workflowStepsArray.filter(ws => this.isChildOf(ws) && ws.submitter && PriorStepTypes.includes(ws.submitter.reference_type));
  }

  get isCheckOrOnlinePaymentOrCheck(): boolean {
    return null;
  }

  afterAppend(): void {
    if (Object.keys(this.viewers).length === 0) {
      this.createDefaultViewers();
    }
  }

  afterDestroy(): void {
    let jumpToClientId = null;
    for (const key in this.actions) {
      jumpToClientId = this.actions[key].jump_to_client_id;
      break;
    }
    this.updateJumpToForEachParent((oldVal) => jumpToClientId);
  }

  nextPaymentLineItemSortOrder(): number {
    return Object.keys(this.payment_line_items).length;
  }

  get paymentLineItems(): Array<PaymentLineItem> {
    return Object.keys(this.payment_line_items).map(x => this.payment_line_items[x]);
  }

  get orderedPaymentLineItems(): Array<PaymentLineItem> {
    return this.paymentLineItems
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  get userDefinedFields(): Array<UserDefinedField> {
    return Object.keys(this.user_defined_fields).map(x => this.user_defined_fields[x]).sort((a, b) => a.sort_order - b.sort_order);
  }

  setSubmitterToOriginalSubmitter(): void {
    if (!this.submitter) {
      new Submitter(this);
    }
    this.submitter.resetToOriginalSubmitter();
  }

  get statusViewersArray(): Viewer[] {
    return ObjectHelper.toArray(this.status_viewers);
  }

  get hasStatusViewers(): boolean {
    return Object.keys(this.status_viewers).length > 0;
  }

  get dataViewersArray(): Viewer[] {
    return ObjectHelper.toArray(this.viewers);
  }

  get hasDataViewers(): boolean {
    return Object.keys(this.viewers).length > 0;
  }

  get moversArray(): Mover[] {
    return ObjectHelper.toArray(this.movers);
  }

  get dataEditorsArray(): DataEditor[] {
    return ObjectHelper.toArray(this.data_editors);
  }

  get notificationsArray(): Notification[] {
    return ObjectHelper.toArray(this.notifications);
  }

  protected isChildOf(parent: WorkflowStep, clientIdsSeen = {}) {
    clientIdsSeen[parent.client_id] = true;
    const childClientIds = Array.from(parent.childClientIds());
    if (childClientIds.includes(this.client_id)) return true;
    for (const childClientId of childClientIds) {
      if (childClientId === '' || !childClientId || clientIdsSeen[childClientId]) continue;
      const childWorkflowStep = this.rootModel.workflow_steps[childClientId];
      if (childWorkflowStep && this.isChildOf(childWorkflowStep, clientIdsSeen)) return true;
    }
    return false;
  }

  get hasConditionalBranches(){
    for (const key in this.actions) {
      const action = this.actions[key];
      if (action.hasConditionalBranches) {
        return true;
      }
    }
    return false;
  }

  *childClientIds(): IterableIterator<string> {
    for (const action of Array.from(this.actionsArray())) {
      for (const jumpToClientId of Array.from(action.jumpToClientIds())) {
        yield jumpToClientId;
      }
    }
  }

  *actionsArray(): IterableIterator<WorkflowStepAction> {
    for (const key in this.actions) {
      yield(this.actions[key]);
    }
  }

  appendDefaultAction(track = true): WorkflowStepAction {
    return this.appendAction({});
  }

  appendAction(props, track = true): WorkflowStepAction {
    const action = new WorkflowStepAction(this, props);
    action.jump_to = WorkflowStepActionJumpToFactory.create(action);
    if (track) {
      this.trackCreationDeletion(action.client_id, null, action.asJson(), action);
    }
    return action;
  }

  appendOverdueSubmissionAction( track = true): OverdueSubmissionAction {
    const overdueAction = new OverdueSubmissionAction(this);
    overdueAction.jump_to = WorkflowStepActionJumpToFactory.create(overdueAction);
    if (track) {
      this.trackCreationDeletion(overdueAction.client_id, null, overdueAction.asJson(), overdueAction);
    }
    return overdueAction;
  }

  setSubmitterType(reference_type: number) {
    this.setSubmitter({
      reference_type,
      reference_id: null,
      name: '',
      first_name: '',
      last_name: '',
      referenceNameOrDefault: '',
      status: null,
    });
  }

  setSubmitter(submitter: object) {
    this.submitter.update(submitter);

    if (this.submitter.reference_type !== PermitteeType.group) {
      for (const id in this.notifications) {
        const notif = this.notifications[id];
        if (notif.trigger_on === NotificationTriggerType.assignment) {
          notif.trigger_on = NotificationTriggerType.beginning;
        }
      }
    }
  }
  /**
   * Find a form element actually linked to this workflow step - do not go to the repo as it
   * may find a form element related to a published form instead of this base form
   */
  findElement(id: string): FormElement {
    if (!this.form_section) return null
    return this.form_section.findElement(id)
  }

  get modelType(): ModelType {
    return ModelType.WorkflowStep;
  }

  get genericType(): GenericType {
    return ModelType.Object
  }

  imposterFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    return new Imposter({ ...this, isCollection: false }, options)
  }

  generateUserDefinedFields() {
    if (this.userDefinedFields.length === 0) {

      [0,1,2,3,4,5,6,7,8,9].forEach(num => {
        const json = {sort_order: num}
        if (num === 0) {
          // payment recipt number dynamic reference type
          json["udf_field_formula"] = `f_${this.id}_drt28`
        } else if (num === 1) {
          // payment total cost dynamic reference type
          json["udf_field_formula"] = `f_${this.id}_drt17`
        }
        let userDefinedField = this.createEmbeddedObject.user_defined_fields(this, json)
        userDefinedField.attachToParent(this)
      })
    }
  }
}
